import { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { toast } from "sonner";

import { uploadBulkMutation } from "../components/bulk/queries/mutations";
import toBase64 from "../utils/images";

interface UseBulkUploadParams {
  onUploaded: (bulkId: string) => void;
}

interface UseBulkUploadResult {
  upload: (file: File) => void;
  progress: number;
  isUploading: boolean;
  isError: boolean;
}

export default function useBulkUpload({
  onUploaded,
}: UseBulkUploadParams): UseBulkUploadResult {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<number>(0);
  const [isReadingFile, setIsReadingFile] = useState<boolean>(false);

  const mutation = useMutation(
    uploadBulkMutation({
      queryClient,
      onProgress: setProgress,
      onUploaded,
    }),
  );

  const upload = (file: File): void => {
    setIsReadingFile(true);

    toBase64(file)
      .then((base64) => {
        setIsReadingFile(false);
        mutation.mutate({
          name: file.name,
          type: file.type,
          size: file.size,
          base64,
        });
      })
      .catch(() => {
        setIsReadingFile(false);
        toast.error(t("editor-errors:errorUpload"));
      });
  };

  return {
    upload,
    progress,
    isUploading: isReadingFile || mutation.isLoading,
    isError: mutation.isError,
  };
}
